import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchQuoteConversionState } from '@features/dashboard/services/dashboard';

export type AgreementConfirmation =
    | { state: 'idle' }
    | { state: 'checking' }
    | { state: 'resolved'; agreementId: string }
    | { state: 'missing' } // bounded poll exhausted with the row visible and unclaimed
    | { state: 'unknown' }; // no visible row, or a transport error — renders nothing

// ~40s of polling: the window must outlast the async chain quote.completed -> Agreement
// created -> agreement.created -> projection.
const MAX_ATTEMPTS = 8;
const INTERVAL_MS = 5000;

export function useAgreementConfirmation(
    quoteId: string | undefined,
    enabled: boolean,
): AgreementConfirmation {
    const [attempts, setAttempts] = useState(0);
    const [stopped, setStopped] = useState(false);
    const settledAtRef = useRef(0);
    const quoteIdRef = useRef(quoteId);

    // A new quoteId is a fresh poll: reset synchronously during render so `enabled`
    // below never evaluates against a stale count carried over from the previous quote.
    if (quoteIdRef.current !== quoteId) {
        quoteIdRef.current = quoteId;
        setAttempts(0);
        setStopped(false);
        settledAtRef.current = 0;
    }

    const query = useQuery({
        queryKey: ['quote-conversion-confirmation', quoteId],
        queryFn: () => fetchQuoteConversionState(quoteId!),
        enabled: enabled && !!quoteId && !stopped && attempts < MAX_ATTEMPTS,
        retry: false,
        // Unconditional: every stop routes through `stopped` -> `enabled`, and a disabled
        // query never evaluates this.
        refetchInterval: INTERVAL_MS,
    });

    const settledAt = Math.max(query.dataUpdatedAt, query.errorUpdatedAt);

    useEffect(() => {
        if (settledAt === 0 || settledAt === settledAtRef.current) {
            return;
        }
        settledAtRef.current = settledAt;

        setAttempts((prev) => prev + 1);

        if (query.isError || (query.isSuccess && query.data.state !== 'pending')) {
            setStopped(true);
        }
    }, [settledAt, query.isError, query.isSuccess, query.data]);

    if (!enabled || !quoteId) {
        return { state: 'idle' };
    }

    if (query.data?.state === 'resolved') {
        return { state: 'resolved', agreementId: query.data.agreementId };
    }

    if (query.isError || query.data?.state === 'unknown') {
        return { state: 'unknown' };
    }

    if (attempts >= MAX_ATTEMPTS) {
        return { state: 'missing' };
    }

    return { state: 'checking' };
}

export { MAX_ATTEMPTS, INTERVAL_MS };
